import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Link,
  LinearProgress,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import KitchenOutlinedIcon from '@mui/icons-material/KitchenOutlined'
import { DishRequirementsDialog } from '@/pages/menu/DishRequirementsDialog'
import { useState } from 'react'
import {
  useDeleteMenuCategory,
  useDeletePackage,
  useMenuCategories,
  useMenuItems,
  useOutlets,
  usePackages,
  useServiceTypes,
  useSessionTypes,
  usePackageVersion,
  usePublishPackageVersion,
} from '@/api/hooks'
import type { CateringPackage, MenuCategory, MenuItem as MenuItemType } from '@/api/types'
import { useAuthStore } from '@/auth/authStore'
import { MenuCategoryDialog, MenuItemDialog } from '@/pages/catalogue/MenuItemDialog'
import { PackageBuilderDialog } from '@/pages/catalogue/PackageBuilderDialog'
import { PackageEditDialog } from '@/pages/catalogue/PackageEditDialog'
import {
  EmptyState,
  ErrorBanner,
  formatMoney,
  formatNumber,
  humanise,
  InlineSpinner,
  PageHeader,
  StatusChip,
} from '@/components/common'
import { useRef } from 'react'
import DownloadIcon from '@mui/icons-material/Download'
import UploadIcon from '@mui/icons-material/Upload'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { api } from '@/api/client'
import type { MenuImportResult, MenuItem as MenuItemView } from '@/api/types'
import { useDeleteMenuItem, useImportMenuItems } from '@/api/hooks'

/** Weight units read as they do on a label: g, kg, ml, l. */
const WEIGHT_UNIT_LABELS: Record<string, string> = { GRAM: 'g', KG: 'kg', ML: 'ml', LITRE: 'l' }

function weightUnitLabel(unit?: string) {
  return WEIGHT_UNIT_LABELS[unit ?? ''] ?? (unit ?? '').toLowerCase()
}

export function MenuPage() {
  const canWrite = useAuthStore((state) => state.canWrite)
  const [tab, setTab] = useState(0)
  const [itemDialog, setItemDialog] = useState(false)
  const [categoryDialog, setCategoryDialog] = useState(false)
  const [packageDialog, setPackageDialog] = useState(false)
  const [imported, setImported] = useState<MenuImportResult | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const importItems = useImportMenuItems()

  return (
    <>
      <PageHeader
        title="Menu and packages"
        subtitle="The catalogue quotations are built from, and the selection rules that govern them"
        actions={
          // Categories are part of the menu, so the dishes tab and the
          // categories tab both ask about the menu; only packages ask about
          // packages. Three answers rather than two, because the third tab was
          // silently taking the packages one.
          canWrite(tab === 1 ? 'package' : 'menu') ? (
            <Stack direction="row" spacing={1}>
              {tab === 0 ? (
                <>
                  <Button startIcon={<DownloadIcon />} onClick={() => api.download(
                    '/api/v1/menu/items/export', undefined, 'menu-items.xlsx')}>
                    Export
                  </Button>
                  <Button
                    startIcon={<UploadIcon />}
                    disabled={importItems.isPending}
                    onClick={() => fileInput.current?.click()}
                  >
                    {importItems.isPending ? 'Importing…' : 'Import'}
                  </Button>
                  {/* Hidden, because a bare file input cannot be styled to sit
                      beside the other buttons. */}
                  <input
                    ref={fileInput}
                    type="file"
                    accept=".xlsx,.xls"
                    hidden
                    onChange={(event) => {
                      const file = event.target.files?.[0]
                      if (file) importItems.mutate(file, { onSuccess: setImported })
                      // Cleared so choosing the same file twice fires again.
                      event.target.value = ''
                    }}
                  />
                  <Button startIcon={<AddIcon />} onClick={() => setCategoryDialog(true)}>
                    New category
                  </Button>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => setItemDialog(true)}>
                    New item
                  </Button>
                </>
              ) : tab === 1 ? (
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setPackageDialog(true)}>
                  New package
                </Button>
              ) : (
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCategoryDialog(true)}>
                  New category
                </Button>
              )}
            </Stack>
          ) : null
        }
      />
      <Card>
        <Tabs value={tab} onChange={(_event, value) => setTab(value)}>
          <Tab label="Menu items" />
          <Tab label="Packages" />
          <Tab label="Categories" />
        </Tabs>
        <Divider />
        <CardContent>
          {tab === 0 ? <MenuItemsTab /> : null}
          {tab === 1 ? <PackagesTab /> : null}
          {tab === 2 ? <CategoriesTab /> : null}
        </CardContent>
      </Card>


      <MenuItemDialog open={itemDialog} onClose={() => setItemDialog(false)} />
      <MenuCategoryDialog open={categoryDialog} onClose={() => setCategoryDialog(false)} />
      <PackageBuilderDialog open={packageDialog} onClose={() => setPackageDialog(false)} />
      {/* What the import actually did. A count alone is not enough: a row that
          could not be taken has to say which row and why, or it is never found. */}
      <Dialog open={Boolean(imported)} onClose={() => setImported(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Menu imported</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            {imported?.created ?? 0} added, {imported?.updated ?? 0} updated
            {imported?.skipped ? `, ${imported.skipped} not taken` : ''}.
          </Typography>
          {imported?.problems?.length ? (
            <Alert severity="warning" sx={{ mt: 1 }}>
              <Typography variant="caption" component="div" sx={{ fontWeight: 600, mb: 0.5 }}>
                These rows were left out; the rest went in.
              </Typography>
              {imported.problems.slice(0, 20).map((problem) => (
                <Typography key={problem} variant="caption" component="div">
                  {problem}
                </Typography>
              ))}
              {imported.problems.length > 20 ? (
                <Typography variant="caption" component="div">
                  …and {imported.problems.length - 20} more.
                </Typography>
              ) : null}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImported(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

function MenuItemsTab() {
  const [deleting, setDeleting] = useState<MenuItemView | null>(null)
  const removeItem = useDeleteMenuItem()
  const canWrite = useAuthStore((state) => state.canWrite)
  const [editing, setEditing] = useState<MenuItemType | null>(null)
  const [mapping, setMapping] = useState<MenuItemType | null>(null)
  const [categoryId, setCategoryId] = useState('')
  const [editingCategory, setEditingCategory] = useState<MenuCategory | null>(null)
  const [dietType, setDietType] = useState('')
  const [q, setQ] = useState('')
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(25)

  const { data: categories } = useMenuCategories()
  const { data, isLoading, error } = useMenuItems({
    categoryId: categoryId || undefined,
    dietType: dietType || undefined,
    q: q || undefined,
    page,
    size,
  })

  return (
    <>
      <ErrorBanner error={error} />
      <MenuCategoryDialog
        open={Boolean(editingCategory)}
        category={editingCategory}
        onClose={() => setEditingCategory(null)}
      />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          label="Search item"
          value={q}
          onChange={(event) => {
            setQ(event.target.value)
            setPage(0)
          }}
          sx={{ minWidth: 240 }}
        />
        <TextField
          select
          label="Category"
          value={categoryId}
          onChange={(event) => {
            setCategoryId(event.target.value)
            setPage(0)
          }}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All categories</MenuItem>
          {categories?.map((category) => (
            <MenuItem key={category.id} value={category.id}>
              {category.name}
              {category.offeredInPackages ? '' : ' — not offered in packages'}
            </MenuItem>
          ))}
        </TextField>
        {/* The one place a category is already named, so the one place to
            correct it - including whether packages may offer its dishes. */}
        {categoryId && canWrite('menu') ? (
          <Button
            size="small"
            onClick={() =>
              setEditingCategory(categories?.find((c) => c.id === categoryId) ?? null)
            }
          >
            Edit category
          </Button>
        ) : null}
        <TextField
          select
          label="Diet"
          value={dietType}
          onChange={(event) => {
            setDietType(event.target.value)
            setPage(0)
          }}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All</MenuItem>
          {['VEG', 'NON_VEG', 'VEGAN', 'JAIN', 'EGG'].map((value) => (
            <MenuItem key={value} value={value}>
              {humanise(value)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {isLoading && <LinearProgress />}
      {data && data.content.length === 0 ? (
        <EmptyState message="No menu items match these filters." />
      ) : (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Item</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Diet</TableCell>
                <TableCell align="right">Serving</TableCell>
                <TableCell align="right">Weight</TableCell>
                <TableCell align="right">Price</TableCell>
                <TableCell align="right">Cost</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.content.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>{item.itemCode}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">{item.name}</Typography>
                      {item.liveCounter && <Chip size="small" label="Live" color="secondary" />}
                    </Stack>
                    {/* What the Tamil menu sheet prints. Shown here so a
                        catalogue half translated can be seen to be. */}
                    {item.nameLocal ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {item.nameLocal}
                      </Typography>
                    ) : null}
                    {/* Sides are part of what goes out, so they read under the dish. */}
                    {item.sideDishes && item.sideDishes.length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        with{' '}
                        {item.sideDishes
                          .map((side) => `${side.name} ${formatNumber(side.quantityGrams)} g`)
                          .join(', ')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{item.categoryName ?? '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={item.dietType === 'VEG' ? 'success' : 'default'}
                      label={humanise(item.dietType)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {formatNumber(item.standardServingQty)} {humanise(item.servingUnit)}
                  </TableCell>
                  <TableCell align="right">
                    {item.weight ? `${formatNumber(item.weight)} ${weightUnitLabel(item.weightUnit)}` : '—'}
                  </TableCell>
                  <TableCell align="right">{formatMoney(item.sellingPrice)}</TableCell>
                  <TableCell align="right">
                    {item.costEstimate === null || item.costEstimate === undefined
                      ? '—'
                      : formatMoney(item.costEstimate)}
                  </TableCell>
                  <TableCell align="right">
                    {canWrite('menu') ? (
                      <>
                        {/* What this dish travels in and is served with, which
                            is a different question from what it is and costs. */}
                        <Tooltip title="Vessels, cutlery and what it is cooked from">
                          <IconButton
                            size="small"
                            aria-label={`Vessels for ${item.name}`}
                            onClick={() => setMapping(item)}
                          >
                            <KitchenOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <IconButton size="small" onClick={() => setEditing(item)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <Tooltip title="Delete. Withdrawn instead where it has been sold.">
                          <IconButton
                            size="small"
                            aria-label={`Delete ${item.name}`}
                            disabled={removeItem.isPending}
                            onClick={() => setDeleting(item)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {data && (
            <TablePagination
              component="div"
              count={data.totalElements}
              page={data.page}
              onPageChange={(_event, newPage) => setPage(newPage)}
              rowsPerPage={size}
              onRowsPerPageChange={(event) => {
                setSize(Number(event.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[25, 50, 100]}
            />
          )}
        </>
      )}

      <MenuItemDialog open={Boolean(editing)} item={editing} onClose={() => setEditing(null)} />
      <DishRequirementsDialog item={mapping} onClose={() => setMapping(null)} />
      <Dialog open={Boolean(deleting)} onClose={() => setDeleting(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete {deleting?.name}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            A dish that has been quoted, ordered, packaged or cooked is withdrawn rather than
            removed, so those records still read correctly. One that has never been used is deleted
            outright.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>Keep it</Button>
          <Button
            color="error"
            variant="contained"
            disabled={removeItem.isPending}
            onClick={() =>
              deleting &&
              removeItem.mutate(deleting.id, { onSuccess: () => setDeleting(null) })
            }
          >
            {removeItem.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

function PackagesTab() {
  const canWrite = useAuthStore((state) => state.canWrite)
  // What is being looked at. Empty is every one of them, which is where the
  // screen opens: a catalogue is browsed before it is searched.
  const [outletId, setOutletId] = useState('')
  const [serviceType, setServiceType] = useState('')
  const [sessionType, setSessionType] = useState('')
  const narrowed = Boolean(outletId || serviceType || sessionType)
  // What was asked for and nothing else. A menu sold at every branch is not an
  // answer to "what is written against this branch", which is the question
  // somebody narrowing the catalogue is asking.
  const filters = {
    outletId: outletId || undefined,
    serviceType: serviceType || undefined,
    sessionType: sessionType || undefined,
  }
  const { data: exactList, isLoading, error } = usePackages({ ...filters, exact: true })
  // The wider answer, only to say how many are being held back and to offer
  // them. Nothing is hidden without the screen admitting it.
  const { data: widerList } = usePackages(filters)
  const [showEverywhere, setShowEverywhere] = useState(false)
  const packages = showEverywhere && narrowed ? widerList : exactList
  const alsoSoldEverywhere = narrowed
    ? Math.max((widerList?.length ?? 0) - (exactList?.length ?? 0), 0)
    : 0
  const { data: outlets } = useOutlets()
  const { data: services } = useServiceTypes()
  const { data: sessions } = useSessionTypes()
  const outletName = new Map((outlets ?? []).map((outlet) => [outlet.id, outlet.name]))
  const [selectedVersionId, setSelectedVersionId] = useState<string | undefined>()
  const { data: version } = usePackageVersion(selectedVersionId)
  const publish = usePublishPackageVersion()
  const remove = useDeletePackage()
  const [editing, setEditing] = useState<CateringPackage | null>(null)
  // What happened, in the package's own words: removed outright, or kept and
  // withdrawn because something was quoted from it.
  const [removed, setRemoved] = useState<string | null>(null)

  return (
    <>
      <ErrorBanner error={error ?? remove.error} />
      {isLoading && <InlineSpinner />}
      {removed ? (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setRemoved(null)}>
          {removed}
        </Alert>
      ) : null}

      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label="Outlet"
          value={outletId}
          onChange={(event) => setOutletId(event.target.value)}
          InputLabelProps={{ shrink: true }}
          SelectProps={{ displayEmpty: true }}
          sx={{ minWidth: 180 }}
        >
          {/* A package tied to no branch is sold at every branch, so narrowing
              to one still shows it: that is what "sold here" means. */}
          <MenuItem value="">Every outlet</MenuItem>
          {(outlets ?? []).map((outlet) => (
            <MenuItem key={outlet.id} value={outlet.id}>
              {outlet.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Service"
          value={serviceType}
          onChange={(event) => setServiceType(event.target.value)}
          InputLabelProps={{ shrink: true }}
          SelectProps={{ displayEmpty: true }}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">Every service</MenuItem>
          {(services ?? []).map((service) => (
            <MenuItem key={service.id} value={service.code}>
              {service.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Session"
          value={sessionType}
          onChange={(event) => setSessionType(event.target.value)}
          InputLabelProps={{ shrink: true }}
          SelectProps={{ displayEmpty: true }}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">Every session</MenuItem>
          {(sessions ?? []).map((session) => (
            <MenuItem key={session.id} value={session.code}>
              {session.name}
            </MenuItem>
          ))}
        </TextField>
        {narrowed ? (
          <Button
            size="small"
            onClick={() => {
              setOutletId('')
              setServiceType('')
              setSessionType('')
              setShowEverywhere(false)
            }}
          >
            Clear
          </Button>
        ) : null}
      </Stack>

      {/* Said out loud rather than left to be noticed: a business whose outdoor
          menus are sold at every branch would otherwise pick a branch and watch
          them disappear with no explanation. */}
      {alsoSoldEverywhere > 0 ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          {showEverywhere
            ? `Including ${alsoSoldEverywhere} sold at every outlet or any session.`
            : `${alsoSoldEverywhere} more ${alsoSoldEverywhere === 1 ? 'is' : 'are'} sold at every outlet or any session, and not written against this one.`}{' '}
          <Link
            component="button"
            type="button"
            variant="caption"
            onClick={() => setShowEverywhere((current) => !current)}
          >
            {showEverywhere ? 'Show only this one' : 'Show them too'}
          </Link>
        </Typography>
      ) : null}

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          {!packages || packages.length === 0 ? (
            <EmptyState
              message={
                outletId || serviceType || sessionType
                  ? 'No package matches this narrowing.'
                  : 'No packages configured.'
              }
            />
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Package</TableCell>
                  <TableCell>Outlet</TableCell>
                  <TableCell>Service</TableCell>
                  <TableCell>Session</TableCell>
                  <TableCell align="right">Per person</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {packages.map((pkg) => (
                  <TableRow
                    key={pkg.id}
                    hover
                    selected={pkg.currentVersionId === selectedVersionId}
                    onClick={() => setSelectedVersionId(pkg.currentVersionId)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2">{pkg.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {pkg.code} · v{pkg.currentVersionNumber ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {/* No branch on the package means every branch sells it,
                          which is what most of them are. */}
                      <Typography variant="caption" color="text.secondary">
                        {pkg.outletId ? outletName.get(pkg.outletId) ?? '—' : 'Every outlet'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {pkg.serviceTypes.map((type) => (
                          <Chip key={type} size="small" variant="outlined" label={humanise(type)} />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {pkg.sessionType === 'ANY' ? 'Any session' : humanise(pkg.sessionType)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{formatMoney(pkg.basePricePerPerson)}</TableCell>
                    <TableCell align="right">
                      {canWrite('package') ? (
                        <Tooltip title="Edit this package">
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              // The row itself selects the package; the pencil edits it.
                              event.stopPropagation()
                              setEditing(pkg)
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      {canWrite('package') ? (
                        <Tooltip title="Delete this package">
                          <IconButton
                            size="small"
                            disabled={remove.isPending}
                            onClick={(event) => {
                              event.stopPropagation()
                              remove.mutate(pkg.id, {
                                onSuccess: (result) =>
                                  setRemoved(
                                    result.removed
                                      ? `${pkg.name} deleted.`
                                      : `${pkg.name} withdrawn — it has been quoted from, so it is`
                                        + ' kept and no longer offered.',
                                  ),
                              })
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <PackageEditDialog pkg={editing} onClose={() => setEditing(null)} />
        </Grid>

        <Grid item xs={12} md={7}>
          {!version ? (
            <EmptyState
              message="Select a package to see its items."
              hint="Each item offers a list of dishes and says how many the customer picks."
            />
          ) : (
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <div>
                    <Typography variant="subtitle1">{version.packageName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Version {version.versionNumber} · {version.serviceTypes.map(humanise).join(', ')}
                      {version.sessionType === 'ANY' ? '' : ` · ${humanise(version.sessionType)}`}
                    </Typography>
                  </div>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <StatusChip status={version.status} />
                    {version.status === 'DRAFT' && canWrite('package') ? (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => publish.mutate(version.id)}
                        disabled={publish.isPending}
                      >
                        Publish
                      </Button>
                    ) : null}
                  </Stack>
                </Stack>
                <ErrorBanner error={publish.error} />

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {/* The guarantee, child percentage and complimentary guests
                      are no longer edited on a package, so they are not shown
                      here either. */}
                  <Detail label="Base price" value={formatMoney(version.basePricePerPerson)} />
                  <Detail label="Tax" value={`${version.taxRatePercent}%`} />
                </Grid>

                <Typography variant="subtitle2" gutterBottom>
                  Items
                </Typography>
                <Table size="small" sx={{ mb: 2 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Item</TableCell>
                      <TableCell align="right">Choose</TableCell>
                      <TableCell align="right">From</TableCell>
                      <TableCell align="right">Extra item</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {version.items.map((rule) => (
                      <TableRow key={rule.id} hover>
                        <TableCell>{rule.itemNumber}</TableCell>
                        <TableCell>{rule.itemLabel}</TableCell>
                        <TableCell align="right">
                          {rule.minSelections === rule.maxSelections
                            ? rule.minSelections
                            : `${rule.minSelections}–${rule.maxSelections}`}
                        </TableCell>
                        <TableCell align="right">{rule.choices.length}</TableCell>
                        <TableCell align="right">
                          {rule.allowExtra ? formatMoney(rule.extraItemPrice) : 'Not allowed'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

    </>
  )
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Grid item xs={6} sm={4}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Grid>
  )
}

/**
 * The category master, in the order it appears on a menu card.
 *
 * <p>A category was only ever a name in a dropdown and two switches buried in a
 * dialog, so what the business actually keeps - and which of them a package may
 * offer, and which a side may be drawn from - could not be read anywhere at
 * once. Half the catalogue hangs off these rows.</p>
 */
function CategoriesTab() {
  const canWrite = useAuthStore((state) => state.canWrite)
  const { data: categories, error } = useMenuCategories()
  const remove = useDeleteMenuCategory()
  const [editing, setEditing] = useState<MenuCategory | null>(null)
  const [removed, setRemoved] = useState<string | null>(null)
  const writable = canWrite('menu')

  return (
    <>
      <ErrorBanner error={error ?? remove.error} />
      {removed ? (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setRemoved(null)}>
          {removed}
        </Alert>
      ) : null}
      <MenuCategoryDialog
        open={Boolean(editing)}
        category={editing}
        onClose={() => setEditing(null)}
      />

      <Typography variant="caption" color="text.secondary">
        The order they appear on a menu card, and what each may be used for.
      </Typography>

      <Table size="small" sx={{ mt: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell>Code</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Course</TableCell>
            <TableCell align="center">In packages</TableCell>
            <TableCell align="center">As a side</TableCell>
            <TableCell align="right">Order</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {(categories ?? []).map((category) => (
            <TableRow
              key={category.id}
              hover={writable}
              sx={{ cursor: writable ? 'pointer' : 'default' }}
              onClick={() => (writable ? setEditing(category) : undefined)}
            >
              <TableCell>{category.code}</TableCell>
              <TableCell>{category.name}</TableCell>
              <TableCell>
                {category.courseType
                  ? category.courseType.replace(/_/g, ' ').toLowerCase()
                  : '—'}
              </TableCell>
              <TableCell align="center">
                {category.offeredInPackages ? (
                  <Chip size="small" variant="outlined" label="Yes" />
                ) : (
                  '—'
                )}
              </TableCell>
              <TableCell align="center">
                {category.offeredAsSideDish ? (
                  <Chip size="small" variant="outlined" label="Yes" />
                ) : (
                  '—'
                )}
              </TableCell>
              <TableCell align="right">{category.sortOrder}</TableCell>
              <TableCell align="right">
                {writable ? (
                  <Tooltip title="Delete this category">
                    <IconButton
                      size="small"
                      disabled={remove.isPending}
                      onClick={(event) => {
                        // The row itself opens the dialog; the bin removes it.
                        event.stopPropagation()
                        remove.mutate(category.id, {
                          onSuccess: (result) =>
                            setRemoved(
                              result.removed
                                ? `${category.name} deleted.`
                                : `${category.name} withdrawn — dishes still sit in it, so it is kept`
                                  + ' and no longer offered.',
                            ),
                        })
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {(categories ?? []).length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          No categories yet. Add one with the button above.
        </Typography>
      ) : null}
    </>
  )
}
